/**
 * Slider Home
 * -----------------------------------------------------------------------------
 * Custom element que controla el slider de banners de la home.
 *
 * Características:
 *  - Scroll horizontal nativo con scroll-snap (soporta swipe en mobile).
 *  - Flechas anterior/siguiente (opcionales, solo si hay > 1 slide).
 *  - Paginación por puntos (opcional).
 *  - Autoplay / scroll automático (opcional, desactivado por defecto).
 *      · Se pausa al pasar el mouse, al enfocar con teclado, cuando la pestaña
 *        no está visible y cuando el usuario interactúa con el slider.
 *      · Respeta la preferencia "prefers-reduced-motion".
 *  - Eventos GA4 / dataLayer opcionales (view_promotion / select_promotion).
 *
 * El alto/ancho del slider lo define la PRIMERA imagen (vía aspect-ratio en CSS,
 * calculado desde Liquid). Las imágenes usan object-fit: contain para no recortarse.
 *
 * Atributos esperados (definidos desde la sección Liquid):
 *  - data-autoplay        : "true" | "false"
 *  - data-autoplay-speed  : milisegundos entre slides (ej. "5000")
 *  - data-loop            : "true" | "false" (vuelve al inicio al llegar al final)
 *  - data-analytics       : "true" | "false" (push de eventos a dataLayer/GA4)
 * -----------------------------------------------------------------------------
 */
if (!customElements.get('slider-home')) {
  class SliderHome extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.autoplayTimer = null;
      this.isPaused = false;
      this._onVisibilityChange = this._onVisibilityChange.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));

      if (!this.track || this.slides.length === 0) return;

      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.prefersReducedMotion;
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
      this.loop = this.dataset.loop === 'true';
      this.analyticsEnabled = this.dataset.analytics === 'true';

      this._bindEvents();
      this._observeSlides();
      this._updateControls(0);

      if (this.autoplayEnabled && this.slides.length > 1) {
        this._startAutoplay();
      }

      // Analítica del primer slide visible.
      if (this.analyticsEnabled) this._trackView(0);
    }

    disconnectedCallback() {
      this._stopAutoplay();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
    }

    /* --------------------------------------------------------------------- */
    /* Eventos                                                               */
    /* --------------------------------------------------------------------- */
    _bindEvents() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => {
          this._pauseAutoplayPermanently();
          this.goTo(this.activeIndex - 1);
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          this._pauseAutoplayPermanently();
          this.goTo(this.activeIndex + 1);
        });
      }

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this._pauseAutoplayPermanently();
          this.goTo(index);
        });
      });

      // Analítica al hacer click en un banner.
      if (this.analyticsEnabled) {
        this.slides.forEach((slide, index) => {
          const link = slide.querySelector('a[href]');
          if (link) {
            link.addEventListener('click', () => this._trackSelect(index));
          }
        });
      }

      // Pausa el autoplay durante la interacción del usuario.
      if (this.autoplayEnabled) {
        this.addEventListener('mouseenter', () => this._pauseAutoplay());
        this.addEventListener('mouseleave', () => this._resumeAutoplay());
        this.addEventListener('focusin', () => this._pauseAutoplay());
        this.addEventListener('focusout', () => this._resumeAutoplay());
        document.addEventListener('visibilitychange', this._onVisibilityChange);
      }
    }

    _onVisibilityChange() {
      if (document.hidden) {
        this._pauseAutoplay();
      } else {
        this._resumeAutoplay();
      }
    }

    /* --------------------------------------------------------------------- */
    /* Detección del slide activo (IntersectionObserver)                     */
    /* --------------------------------------------------------------------- */
    _observeSlides() {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              const index = this.slides.indexOf(entry.target);
              if (index !== -1 && index !== this.activeIndex) {
                this.activeIndex = index;
                this._updateControls(index);
                if (this.analyticsEnabled) this._trackView(index);
              }
            }
          });
        },
        { root: this.track, threshold: [0.6] }
      );

      this.slides.forEach((slide) => this.intersectionObserver.observe(slide));
    }

    /* --------------------------------------------------------------------- */
    /* Navegación                                                            */
    /* --------------------------------------------------------------------- */
    goTo(index) {
      const total = this.slides.length;
      let target = index;

      if (target < 0) target = this.loop ? total - 1 : 0;
      if (target > total - 1) target = this.loop ? 0 : total - 1;

      const slide = this.slides[target];
      if (!slide) return;

      this.track.scrollTo({
        left: slide.offsetLeft - this.track.offsetLeft,
        behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
      });

      this.activeIndex = target;
      this._updateControls(target);
    }

    _updateControls(index) {
      // Puntos de paginación.
      this.dots.forEach((dot, i) => {
        const isActive = i === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      // Estado de las flechas (cuando no hay loop).
      if (!this.loop) {
        if (this.prevButton) this.prevButton.disabled = index === 0;
        if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
      }
    }

    /* --------------------------------------------------------------------- */
    /* Autoplay                                                              */
    /* --------------------------------------------------------------------- */
    _startAutoplay() {
      this._stopAutoplay();
      this.autoplayTimer = window.setInterval(() => {
        if (this.isPaused) return;
        const next = this.activeIndex + 1;
        if (next > this.slides.length - 1 && !this.loop) {
          this.goTo(0);
        } else {
          this.goTo(next);
        }
      }, this.autoplaySpeed);
    }

    _stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    _pauseAutoplay() {
      this.isPaused = true;
    }

    _resumeAutoplay() {
      this.isPaused = false;
    }

    // Detiene el autoplay tras una interacción explícita (flecha / punto).
    _pauseAutoplayPermanently() {
      if (this.autoplayEnabled) this._stopAutoplay();
    }

    /* --------------------------------------------------------------------- */
    /* Analítica GA4 / dataLayer                                             */
    /* --------------------------------------------------------------------- */
    _slidePromotion(index) {
      const slide = this.slides[index];
      if (!slide) return null;
      return {
        promotion_id: slide.dataset.promotionId || '',
        promotion_name: slide.dataset.promotionName || '',
        creative_slot: String(index + 1),
        location_id: this.id || 'slider-home',
      };
    }

    _trackView(index) {
      const promo = this._slidePromotion(index);
      if (!promo) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'view_promotion',
        ecommerce: { items: [promo] },
      });
    }

    _trackSelect(index) {
      const promo = this._slidePromotion(index);
      if (!promo) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'select_promotion',
        ecommerce: { items: [promo] },
      });
    }
  }

  customElements.define('slider-home', SliderHome);

  /* ----------------------------------------------------------------------- */
  /* Integración con el editor de temas de Shopify                           */
  /* ----------------------------------------------------------------------- */
  document.addEventListener('shopify:block:select', (event) => {
    const slider = event.target.closest('slider-home');
    if (!slider) return;
    const slide = event.target.closest('[data-slide]');
    if (slide && typeof slider.goTo === 'function') {
      const slides = Array.from(slider.querySelectorAll('[data-slide]'));
      slider.goTo(slides.indexOf(slide));
    }
  });
}
